import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Code, ArrowBack, Warning, Upload, CloudDownload } from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { adminAPI } from '../services/api';

const DevelopmentPage: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { language } = useLanguage();
  const [scenarioFile, setScenarioFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [progress, setProgress] = useState<string>('');
  const [progressStep, setProgressStep] = useState<number>(0);
  const [totalSteps] = useState<number>(2);

  const handleAnalyze = async () => {
    if (!scenarioFile) {
      setError(language === 'tr' ? 'Lütfen senaryo PDF dosyasını yükleyin.' : 'Please upload scenario PDF file.');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);
    setProgressStep(0);
    setProgress(language === 'tr' ? 'Senaryo yükleniyor...' : 'Uploading scenario...');

    let progressInterval: NodeJS.Timeout | null = null;

    try {
      // İlerleme simülasyonu (gerçek backend süresini tahmin etmek için)
      progressInterval = setInterval(() => {
        setProgressStep((prev) => {
          if (prev < totalSteps) {
            if (prev < 1) {
              setProgress(language === 'tr' 
                ? '1. Aşama: Script Supervisor dökümü çıkarılıyor...' 
                : 'Step 1: Extracting Script Supervisor breakdown...');
            } else if (prev < 2) {
              setProgress(language === 'tr' 
                ? '2. Aşama: Sahne özetleri oluşturuluyor...' 
                : 'Step 2: Generating scene summaries...');
            }
            return Math.min(prev + 0.1, totalSteps);
          }
          return prev;
        });
      }, 2000); // Her 2 saniyede bir ilerleme göster

      setProgressStep(0);
      setProgress(language === 'tr' 
        ? '1. Aşama: Script Supervisor dökümü çıkarılıyor...' 
        : 'Step 1: Extracting Script Supervisor breakdown...');

      const blob = await adminAPI.analyzeScenarioDevelopment(scenarioFile);
      
      if (progressInterval) clearInterval(progressInterval);
      setProgressStep(totalSteps);
      setProgress(language === 'tr' 
        ? '✅ Analiz tamamlandı! Excel dosyası hazırlanıyor...' 
        : '✅ Analysis completed! Preparing Excel file...');
      
      // Excel dosyasını indir
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${scenarioFile.name.replace('.pdf', '')}_analiz.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccess(true);
      setScenarioFile(null);
      
      // Başarı mesajından sonra progress'i sıfırla
      setTimeout(() => {
        setProgress('');
        setProgressStep(0);
      }, 3000);
    } catch (err: any) {
      if (progressInterval) clearInterval(progressInterval);
      console.error('Analiz hatası:', err);
      console.error('Hata detayları:', err.response?.data);
      
      let errorMessage = language === 'tr' ? 'Analiz sırasında bir hata oluştu.' : 'An error occurred during analysis.';
      
      if (err.response?.status === 500) {
        errorMessage = err.response?.data?.detail || 
          (language === 'tr' 
            ? 'Sunucu hatası: Lütfen daha sonra tekrar deneyin.'
            : 'Server error: Please try again later.');
      } else if (err.response?.status === 400) {
        errorMessage = err.response?.data?.detail || 
          (language === 'tr' ? 'Geçersiz istek: Dosya kontrol edin.' : 'Invalid request: Check file.');
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setProgress('');
      setProgressStep(0);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 transition-colors"
          >
            <ArrowBack style={{ width: '20px', height: '20px' }} />
            <span>{language === 'tr' ? 'Admin Paneline Dön' : 'Back to Admin Panel'}</span>
          </button>
          
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-yellow-500 dark:bg-yellow-600 rounded-lg flex items-center justify-center">
              <Code style={{ width: '28px', height: '28px', color: 'white' }} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                {language === 'tr' ? 'Geliştirme Sayfası' : 'Development Page'}
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                {language === 'tr' ? 'Bu sayfa geliştirme aşamasındadır' : 'This page is under development'}
              </p>
            </div>
          </div>
        </div>

        {/* Warning Banner */}
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-8">
          <div className="flex items-start space-x-3">
            <Warning className="text-yellow-600 dark:text-yellow-400" style={{ width: '24px', height: '24px', marginTop: '2px' }} />
            <div>
              <h3 className="font-semibold text-yellow-800 dark:text-yellow-300 mb-1">
                {language === 'tr' ? 'Geliştirme Aşamasında' : 'Under Development'}
              </h3>
              <p className="text-sm text-yellow-700 dark:text-yellow-400">
                {language === 'tr' 
                  ? 'Bu sayfa henüz tamamlanmamıştır ve aktif olarak geliştirilmektedir. Lütfen dikkatli kullanın.' 
                  : 'This page is not yet complete and is actively under development. Please use with caution.'}
              </p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-8 border border-gray-200 dark:border-gray-800">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              {language === 'tr' ? 'Script Supervisor Dökümü ve Sahne Özetleri' : 'Script Supervisor Breakdown and Scene Summaries'}
            </h2>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 mb-6">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {language === 'tr' 
                  ? 'Bu araç, senaryo PDF\'inizi Script Supervisor protokolüne göre analiz eder: önce sahne bazlı döküm çıkarır (Sahne No, Mekan, Zaman, Oyuncular, Aksesuar vb.), sonra her sahne için prompt metnine göre detaylı özet ekler ve sonuçları Excel formatında indirebilirsiniz.'
                  : 'This tool analyzes your scenario PDF according to Script Supervisor protocol: first extracts scene-based breakdown (Scene No, Location, Time, Actors, Props, etc.), then adds detailed summaries for each scene according to the prompt text, and allows you to download results in Excel format.'}
              </p>
              
              <div className="space-y-4">
                {/* Senaryo PDF Yükleme */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {language === 'tr' ? 'Senaryo PDF' : 'Scenario PDF'}
                  </label>
                  <div className="flex items-center space-x-4">
                    <label className="flex-1 cursor-pointer">
                      <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-gray-400 dark:hover:border-gray-500 transition-colors">
                        <Upload className="mx-auto mb-2" style={{ width: '32px', height: '32px', color: theme === 'dark' ? '#94a3b8' : '#64748b' }} />
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {scenarioFile 
                            ? scenarioFile.name 
                            : (language === 'tr' ? 'Senaryo PDF dosyasını seçin' : 'Select scenario PDF file')}
                        </p>
                      </div>
                      <input
                        type="file"
                        accept=".pdf"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) setScenarioFile(file);
                        }}
                      />
                    </label>
                    {scenarioFile && (
                      <button
                        onClick={() => setScenarioFile(null)}
                        className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>

                {/* İlerleme Göstergesi */}
                {loading && (
                  <div className="p-4 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {progress || (language === 'tr' ? 'Analiz ediliyor...' : 'Analyzing...')}
                        </span>
                        <span className="text-xs text-gray-600 dark:text-gray-400">
                          {Math.min(Math.round((progressStep / totalSteps) * 100), 95)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                        <div 
                          className="bg-gray-600 dark:bg-gray-400 h-2.5 rounded-full transition-all duration-500 ease-out"
                          style={{ width: `${Math.min((progressStep / totalSteps) * 100, 95)}%` }}
                        ></div>
                      </div>
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                      <div className={progressStep >= 0 ? 'font-semibold' : 'opacity-50'}>
                        {language === 'tr' ? '✓ Senaryo yüklendi' : '✓ Scenario uploaded'}
                      </div>
                      <div className={progressStep >= 1 ? 'font-semibold' : 'opacity-50'}>
                        {language === 'tr' 
                          ? (progressStep >= 1 ? '✓ Script Supervisor dökümü tamamlandı' : '○ Script Supervisor dökümü çıkarılıyor...')
                          : (progressStep >= 1 ? '✓ Script Supervisor breakdown completed' : '○ Extracting Script Supervisor breakdown...')}
                      </div>
                      <div className={progressStep >= 2 ? 'font-semibold' : 'opacity-50'}>
                        {language === 'tr' 
                          ? (progressStep >= 2 ? '✓ Sahne özetleri oluşturuldu' : '○ Sahne özetleri oluşturuluyor...')
                          : (progressStep >= 2 ? '✓ Scene summaries generated' : '○ Generating scene summaries...')}
                      </div>
                    </div>
                  </div>
                )}

                {/* Hata ve Başarı Mesajları */}
                {error && (
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
                  </div>
                )}

                {success && (
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <p className="text-sm text-green-700 dark:text-green-400">
                      {language === 'tr' ? 'Analiz başarıyla tamamlandı! Excel dosyası indiriliyor...' : 'Analysis completed successfully! Excel file is downloading...'}
                    </p>
                  </div>
                )}

                {/* Analiz Butonu */}
                <button
                  onClick={handleAnalyze}
                  disabled={loading || !scenarioFile}
                  className="w-full py-3 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white dark:border-gray-900"></div>
                      <span>{language === 'tr' ? 'Analiz ediliyor...' : 'Analyzing...'}</span>
                    </>
                  ) : (
                    <>
                      <CloudDownload style={{ width: '20px', height: '20px' }} />
                      <span>{language === 'tr' ? 'Analiz Et ve Excel İndir' : 'Analyze and Download Excel'}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Info */}
        <div className="mt-8 text-center text-sm text-gray-500 dark:text-gray-400">
          <p>
            {language === 'tr' 
              ? 'Sayfa: /development | Durum: Geliştirme Aşamasında' 
              : 'Page: /development | Status: Under Development'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default DevelopmentPage;

